import { useId } from 'react';
import { createPortal } from 'react-dom';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { MASTERY } from '@padhai/shared';

/**
 * The shared pieces every screen is built from. Kept in one file while it is
 * small; split when it stops fitting on a screen.
 *
 * Everything here renders a real element — a button is a <button>, a field has
 * a <label> — because the spec requires keyboard access and visible focus
 * (section 9), and because that is cheaper than rebuilding it later.
 */

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/* ------------------------------------------------------------------ card -- */

export function Card({
  children,
  className,
  as: Tag = 'section',
}: {
  children: ReactNode;
  className?: string;
  as?: 'section' | 'article' | 'div';
}) {
  return (
    // The hairline lives in --shadow-card, so no border here; two edges read
    // as a double line on a high-density screen.
    <Tag className={cx('rounded-card bg-surface shadow-card', className)}>{children}</Tag>
  );
}

export function CardHeader({
  title,
  hint,
  action,
}: {
  title: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3 sm:px-5">
      <div className="min-w-0">
        <h2 className="text-base font-semibold">{title}</h2>
        {hint ? <p className="mt-0.5 text-sm text-ink-soft">{hint}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/* ---------------------------------------------------------------- button -- */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-gradient text-on-brand border-transparent shadow-brand hover:brightness-110 active:brightness-95',
  secondary:
    'bg-surface text-ink border-line-strong hover:border-brand-line hover:bg-brand-soft/40',
  ghost: 'bg-transparent text-ink-soft border-transparent hover:bg-surface-sunken hover:text-ink',
  danger: 'bg-weak text-on-brand border-transparent hover:opacity-90',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  // 44px minimum height on anything a student taps on a phone.
  sm: 'min-h-9 px-3 text-sm',
  md: 'min-h-11 px-4 text-sm',
  lg: 'min-h-12 px-5 text-base',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  className,
  loading = false,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-control border font-medium',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-55',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

/* ----------------------------------------------------------------- badge -- */

type Tone = 'neutral' | 'brand' | 'good' | 'warn' | 'weak';

const TONES: Record<Tone, string> = {
  neutral: 'bg-surface-sunken text-ink-soft border-line',
  brand: 'bg-brand-soft text-brand-strong border-brand-line',
  good: 'bg-good-soft text-good border-good/30',
  warn: 'bg-warn-soft text-warn border-warn/30',
  weak: 'bg-weak-soft text-weak border-weak/30',
};

export function Badge({
  tone = 'neutral',
  children,
  icon,
}: {
  tone?: Tone;
  children: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        TONES[tone],
      )}
    >
      {icon}
      {children}
    </span>
  );
}

/* -------------------------------------------------------------- progress -- */

/**
 * Mastery color ramp. Weak work is amber rather than red: a 30% score is a
 * place to start, not a failure, and the student sees this number every day.
 */
export function masteryTone(score: number | null): Tone {
  if (score === null) return 'neutral';
  if (score >= MASTERY.strongAtOrAbove) return 'good';
  if (score > MASTERY.weakAtOrBelow) return 'warn';
  return 'weak';
}

export function masteryColor(score: number | null): string {
  if (score === null) return 'var(--color-line-strong)';
  if (score >= MASTERY.strongAtOrAbove) return 'var(--color-good)';
  if (score > MASTERY.weakAtOrBelow) return 'var(--color-warn)';
  return 'var(--color-weak)';
}

/**
 * A mastery bar.
 *
 * It fills its container, so give it width by wrapping it, not by passing a
 * width in `className`: two width utilities on one element are resolved by the
 * order Tailwind emits them, not the order they are written, and `w-full` was
 * quietly winning. That is what left every sub-topic name on My progress
 * squeezed to zero width next to a full-width bar.
 */
export function ProgressBar({
  value,
  label,
  className,
  color,
}: {
  value: number | null;
  label: string;
  /** Spacing and visibility only. Width belongs on the container. */
  className?: string;
  /**
   * Overrides the mastery ramp. Not every bar is a score: syllabus coverage at
   * 18% early in the year is on track, and coloring it red says otherwise.
   */
  color?: string;
}) {
  const pct = value ?? 0;
  return (
    <div
      className={cx('h-2 w-full overflow-hidden rounded-full bg-surface-sunken', className)}
      role="progressbar"
      aria-valuenow={value ?? 0}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div
        className="h-full rounded-full transition-[width] duration-500"
        style={{ width: `${pct}%`, backgroundColor: color ?? masteryColor(value) }}
      />
    </div>
  );
}

/**
 * A compact ring for a single number, used on topic rows.
 *
 * The arc is drawn with a gradient and animates from empty on mount, which is
 * what separates this from a plain donut. A topic never attempted shows a
 * dashed track rather than a zero: nothing has gone wrong, it has not started.
 */
export function ProgressRing({
  value,
  size = 46,
  label,
}: {
  value: number | null;
  size?: number;
  label: string;
}) {
  const stroke = 4;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = ((value ?? 0) / 100) * circumference;
  const gradientId = useId();

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={value === null ? `${label}: not started` : `${label}: ${value} percent`}
      className="shrink-0"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={masteryColor(value)} stopOpacity="0.65" />
          <stop offset="100%" stopColor={masteryColor(value)} />
        </linearGradient>
      </defs>

      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--color-surface-sunken)"
        strokeWidth={stroke}
        strokeDasharray={value === null ? '2 4' : undefined}
        strokeLinecap="round"
      />
      {value === null ? null : (
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - filled}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            animation: 'padhai-ring 0.9s var(--ease-out-soft) both',
            // Consumed by the keyframes below so each ring draws to its own value.
            ['--ring-to' as string]: `${circumference - filled}`,
            ['--ring-from' as string]: `${circumference}`,
          }}
        />
      )}
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        className={cx(
          'tabular text-[11px] font-semibold',
          value === null ? 'fill-[var(--color-ink-faint)]' : 'fill-[var(--color-ink)]',
        )}
      >
        {value === null ? '–' : value}
      </text>
    </svg>
  );
}

/* ------------------------------------------------------------------ stat -- */

export function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
  icon,
  onClick,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  /** A tinted tile at the card's right, for a number that wants a face. */
  icon?: ReactNode;
  /**
   * Makes the whole card a button. A number the reader is expected to act on
   * should be reachable from where it is shown — "11 waiting for your approval"
   * sat next to a tab the teacher then had to find on their own.
   */
  onClick?: () => void;
}) {
  const accent =
    tone === 'good'
      ? 'text-good'
      : tone === 'warn'
        ? 'text-warn'
        : tone === 'weak'
          ? 'text-weak'
          : // 'brand' used to fall through to this branch, so three callers asked
            // for the brand tone and silently got plain ink.
            tone === 'brand'
            ? 'text-brand-strong'
            : 'text-ink';

  const text = (
    <>
      <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.07em] text-ink-faint">
        {label}
      </p>
      <p className={cx('tabular mt-1.5 text-[1.75rem] font-semibold leading-none', accent)}>
        {value}
      </p>
      {hint ? <p className="mt-1.5 text-xs leading-snug text-ink-soft">{hint}</p> : null}
    </>
  );
  const body = icon ? (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">{text}</div>
      <span
        aria-hidden="true"
        className="flex size-10 shrink-0 items-center justify-center rounded-control bg-surface-sunken"
      >
        {icon}
      </span>
    </div>
  ) : (
    text
  );

  const shell = 'rounded-card bg-surface px-3.5 py-3 shadow-card sm:px-4';

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cx(shell, 'lift text-left hover:bg-brand-soft/40')}
      >
        {body}
      </button>
    );
  }

  return <div className={shell}>{body}</div>;
}

/* ----------------------------------------------------------------- state -- */

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="px-4 py-10 text-center">
      <p className="font-medium">{title}</p>
      <p className="mx-auto mt-1 max-w-sm text-sm text-ink-soft">{body}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('shimmer rounded-control', className)} aria-hidden="true" />;
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-control border border-weak/40 bg-weak-soft px-3 py-2 text-sm text-weak"
    >
      {children}
    </p>
  );
}

/* ----------------------------------------------------------------- toast -- */

export type ToastTone = 'good' | 'error';

export interface ToastMessage {
  message: string;
  tone: ToastTone;
}

/**
 * What an action did, or why it did not. Pinned to the bottom of the screen
 * rather than placed at the top of the page: a teacher acting on the last
 * rows of an eighteen-row table at 360px never saw a confirmation drawn
 * above the table, and a failure looked exactly like a success because both
 * were green. A failure is an alert in the weak tone and stays longer.
 *
 * Rendered into <body> through a portal. Inside the page it was positioned
 * against the page's own entrance animation rather than the screen — an
 * element with a transform animation in effect, even one finished at
 * `transform: none`, is the containing block for anything `fixed` inside
 * it — so on a long page the toast sat at the bottom of the page, out of view.
 */
export function Toast({ toast, onDismiss }: { toast: ToastMessage | null; onDismiss: () => void }) {
  if (!toast) return null;
  const error = toast.tone === 'error';
  return createPortal(
    <div className="no-print pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-4 pb-4">
      {/*
       * The live region holds the message alone, so what is announced (and
       * what a test reads) is the sentence, not the icon and the close button.
       */}
      <div
        className={cx(
          'pointer-events-auto flex w-full max-w-md items-start gap-2.5 rounded-control border px-3.5 py-2.5 text-sm text-ink shadow-raised',
          error ? 'border-weak/50 bg-weak-soft' : 'border-good/40 bg-good-soft',
        )}
      >
        <span
          aria-hidden="true"
          className={cx('mt-0.5 font-semibold', error ? 'text-weak' : 'text-good')}
        >
          {error ? '!' : '✓'}
        </span>
        <p role={error ? 'alert' : 'status'} className="min-w-0 flex-1">
          {toast.message}
        </p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-my-1 -mr-1.5 inline-flex size-8 shrink-0 items-center justify-center rounded-control text-ink-soft hover:bg-surface/60 hover:text-ink"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </div>,
    document.body,
  );
}

/** How long a toast stays: long enough to read, longer when something failed. */
export function toastDuration(toast: ToastMessage): number {
  return toast.tone === 'error' ? 9000 : 4000;
}

/* ----------------------------------------------------------------- icons -- */

/**
 * Inline SVG rather than an icon package: there are few enough to hand-write,
 * and it is one less thing to download on a slow connection.
 */
export function LockIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={cx('size-4', className)} aria-hidden="true" fill="none">
      <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5.5 7V5a2.5 2.5 0 015 0v2" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

export function SparkIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={cx('size-4', className)} aria-hidden="true">
      <path
        d="M8 1.5c.8 2.6 2 3.8 4.5 4.6C10 6.9 8.8 8.1 8 10.7c-.8-2.6-2-3.8-4.5-4.6C6 5.3 7.2 4.1 8 1.5z"
        fill="currentColor"
      />
      <path
        d="M12.5 10c.4 1.3 1 1.9 2.3 2.3-1.3.4-1.9 1-2.3 2.3-.4-1.3-1-1.9-2.3-2.3 1.3-.4 1.9-1 2.3-2.3z"
        fill="currentColor"
        opacity="0.6"
      />
    </svg>
  );
}

export function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={cx('size-4', className)} aria-hidden="true" fill="none">
      <path
        d="M6 3.5L10.5 8 6 12.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ----------------------------------------------------------------- forms -- */

/** 16px text so a phone does not zoom the page when the field takes focus. */
export const inputClass =
  'w-full rounded-control border border-line-strong bg-surface px-3 py-3 text-base outline-none transition-colors placeholder:text-ink-faint focus:border-brand';

/** The compact version for dense admin forms; still 16px text, still 44px tall. */
export const inputClassCompact =
  'w-full min-h-11 rounded-control border border-line-strong bg-surface px-3 py-2 text-base outline-none transition-colors placeholder:text-ink-faint focus:border-brand';

export function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 flex items-baseline justify-between gap-2 text-sm">
        <span className="font-medium">{label}</span>
        {hint ? <span className="text-xs text-ink-faint">{hint}</span> : null}
      </label>
      {children}
    </div>
  );
}

/**
 * A credential shown exactly once. Large, monospaced, spaced out so a PIN can
 * be read across a desk, and selectable so a password can be copied.
 */
export function SecretText({ value }: { value: string }) {
  return (
    <span className="tabular select-all font-mono text-[1.6rem] font-semibold tracking-[0.18em] text-ink">
      {value}
    </span>
  );
}
